/**
 * Tokens y fragmentos de estilo del rediseño 2026-09 del módulo de Chats/Tickets.
 *
 * La fila de la bandeja (`components/TicketListItemCustom`) definió el lenguaje
 * visual "tarjeta elevada". Este módulo lo extrae en funciones puras que
 * devuelven fragmentos JSS para hacer spread dentro de cualquier `makeStyles`:
 *
 *   const useStyles = makeStyles((theme) => ({
 *     miTarjeta: {
 *       ...cardSurface(theme),
 *       ...liftHover(theme),
 *       padding: theme.spacing(2),
 *     },
 *   }));
 *
 * No tiene estado ni JSX: sólo objetos de estilo. Cambiar el look del módulo
 * entero se hace desde aquí.
 *
 * Para los modales del módulo se exporta además el hook `useDialogStyles`
 * (ver más abajo): `classes={{ paper: useDialogStyles().paper }}` en el <Dialog>.
 */

import { makeStyles } from "@material-ui/core/styles";

const isDark = (theme) =>
  theme.mode === "dark" || theme?.palette?.type === "dark";

// ── Constantes crudas (para casos que no encajan en un fragmento) ──────────────

export const RADII = {
  surface: 14, // tarjetas / paneles
  surfaceSm: 12, // idem en breakpoint sm
  control: 10, // botones-icono, chips cuadrados
  pill: 999, // píldoras, contadores, badges
};

export const BORDERS = {
  base: (theme) => `1px solid ${isDark(theme) ? "#263248" : "#e5e8f0"}`,
  hover: (theme) => (isDark(theme) ? "#33425e" : "#d3d8e4"),
};

export const SHADOWS = {
  base: (theme) =>
    isDark(theme)
      ? "0 1px 2px rgba(0,0,0,0.4)"
      : "0 1px 2px rgba(20,28,48,0.06)",
  lift: (theme) =>
    isDark(theme)
      ? "0 14px 30px -10px rgba(0,0,0,0.6), 0 4px 10px -4px rgba(0,0,0,0.5)"
      : "0 10px 26px -8px rgba(20,28,48,0.22), 0 3px 8px -4px rgba(20,28,48,0.14)",
  ring: (theme) =>
    `0 0 0 1px ${theme.palette.primary.main}, 0 10px 26px -10px rgba(20,28,48,0.28)`,
};

export const TINT = {
  brand: (theme) =>
    isDark(theme) ? "rgba(129,140,248,0.18)" : "#eef0ff",
  brandWash: (theme) =>
    isDark(theme)
      ? "linear-gradient(90deg, rgba(129,140,248,0.16), transparent 62%)"
      : "linear-gradient(90deg, #eef0ff, transparent 62%)",
};

export const TRANSITION =
  "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease";

/**
 * Tokens del hilo de conversación (Tickets). WhatsApp-web como referencia,
 * con superficies más limpias (sin wallpaper) para un look SaaS.
 */
export const CHAT = {
  paneBg: (theme) => (isDark(theme) ? "#0b141a" : "#efeae2"),
  panePattern: (theme) =>
    isDark(theme)
      ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='340' height='340' viewBox='0 0 340 340'%3E%3Cg fill='none' stroke='%23ffffff' stroke-width='1.05' opacity='0.055'%3E%3Ccircle cx='28' cy='32' r='5'/%3E%3Ccircle cx='86' cy='24' r='3'/%3E%3Cpath d='M52 58c8-6 16 2 8 8'/%3E%3Cpath d='M118 40l6 7-7 5'/%3E%3Cpath d='M160 28c6 0 9 8 2 10M154 22h12M160 16v12'/%3E%3Ccircle cx='210' cy='36' r='4'/%3E%3Cpath d='M246 22c10 0 10 16 0 16s-10-16 0-16z'/%3E%3Cpath d='M288 40c8-10 20-2 12 8'/%3E%3Cpath d='M24 98h14M31 91v14'/%3E%3Ccircle cx='78' cy='104' r='6'/%3E%3Cpath d='M120 92c12 0 12 18 0 18s-12-18 0-18z'/%3E%3Cpath d='M168 108l8-10 8 10h-5v10h-6v-10z'/%3E%3Cpath d='M220 96c8 0 12 10 4 14'/%3E%3Ccircle cx='268' cy='110' r='3'/%3E%3Cpath d='M300 94l10 6-10 6z'/%3E%3Cpath d='M36 160c10-8 22 6 8 12'/%3E%3Ccircle cx='92' cy='168' r='5'/%3E%3Cpath d='M130 152h16M138 144v16'/%3E%3Cpath d='M176 164c7-9 18-2 11 8'/%3E%3Cpath d='M228 150c8 4 8 16-2 16s-10-12-2-16 12 0 12 0'/%3E%3Ccircle cx='276' cy='158' r='4'/%3E%3Cpath d='M312 170l-8-12h16z'/%3E%3Cpath d='M22 220c12 0 12 14 0 14'/%3E%3Cpath d='M68 214l8 8-8 8'/%3E%3Ccircle cx='116' cy='226' r='6'/%3E%3Cpath d='M158 212c10-8 20 8 6 12'/%3E%3Cpath d='M210 228h14M217 221v14'/%3E%3Cpath d='M254 214c8 0 10 12 2 14'/%3E%3Ccircle cx='304' cy='222' r='3'/%3E%3Cpath d='M40 280c8-10 20-2 12 8'/%3E%3Ccircle cx='88' cy='292' r='4'/%3E%3Cpath d='M128 276c10 0 10 16 0 16s-10-16 0-16z'/%3E%3Cpath d='M176 290l7-9 7 9h-4v8h-6v-8z'/%3E%3Cpath d='M230 278h16M238 270v16'/%3E%3Ccircle cx='280' cy='286' r='5'/%3E%3Cpath d='M318 274c8 6 2 16-6 10'/%3E%3C/g%3E%3C/svg%3E\")"
      : "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='340' height='340' viewBox='0 0 340 340'%3E%3Cg fill='none' stroke='%23111b21' stroke-width='1.05' opacity='0.08'%3E%3Ccircle cx='28' cy='32' r='5'/%3E%3Ccircle cx='86' cy='24' r='3'/%3E%3Cpath d='M52 58c8-6 16 2 8 8'/%3E%3Cpath d='M118 40l6 7-7 5'/%3E%3Cpath d='M160 28c6 0 9 8 2 10M154 22h12M160 16v12'/%3E%3Ccircle cx='210' cy='36' r='4'/%3E%3Cpath d='M246 22c10 0 10 16 0 16s-10-16 0-16z'/%3E%3Cpath d='M288 40c8-10 20-2 12 8'/%3E%3Cpath d='M24 98h14M31 91v14'/%3E%3Ccircle cx='78' cy='104' r='6'/%3E%3Cpath d='M120 92c12 0 12 18 0 18s-12-18 0-18z'/%3E%3Cpath d='M168 108l8-10 8 10h-5v10h-6v-10z'/%3E%3Cpath d='M220 96c8 0 12 10 4 14'/%3E%3Ccircle cx='268' cy='110' r='3'/%3E%3Cpath d='M300 94l10 6-10 6z'/%3E%3Cpath d='M36 160c10-8 22 6 8 12'/%3E%3Ccircle cx='92' cy='168' r='5'/%3E%3Cpath d='M130 152h16M138 144v16'/%3E%3Cpath d='M176 164c7-9 18-2 11 8'/%3E%3Cpath d='M228 150c8 4 8 16-2 16s-10-12-2-16 12 0 12 0'/%3E%3Ccircle cx='276' cy='158' r='4'/%3E%3Cpath d='M312 170l-8-12h16z'/%3E%3Cpath d='M22 220c12 0 12 14 0 14'/%3E%3Cpath d='M68 214l8 8-8 8'/%3E%3Ccircle cx='116' cy='226' r='6'/%3E%3Cpath d='M158 212c10-8 20 8 6 12'/%3E%3Cpath d='M210 228h14M217 221v14'/%3E%3Cpath d='M254 214c8 0 10 12 2 14'/%3E%3Ccircle cx='304' cy='222' r='3'/%3E%3Cpath d='M40 280c8-10 20-2 12 8'/%3E%3Ccircle cx='88' cy='292' r='4'/%3E%3Cpath d='M128 276c10 0 10 16 0 16s-10-16 0-16z'/%3E%3Cpath d='M176 290l7-9 7 9h-4v8h-6v-8z'/%3E%3Cpath d='M230 278h16M238 270v16'/%3E%3Ccircle cx='280' cy='286' r='5'/%3E%3Cpath d='M318 274c8 6 2 16-6 10'/%3E%3C/g%3E%3C/svg%3E\")",
  incomingBg: (theme) => (isDark(theme) ? "#1f2937" : "#ffffff"),
  incomingText: (theme) => (isDark(theme) ? "#f3f4f6" : "#111b21"),
  outgoingBg: (theme) => (isDark(theme) ? "#005c4b" : "#d9fdd3"),
  outgoingText: (theme) => (isDark(theme) ? "#e9ffef" : "#111b21"),
  timestampIn: (theme) => (isDark(theme) ? "rgba(243,244,246,0.55)" : "#667781"),
  timestampOut: (theme) => (isDark(theme) ? "rgba(233,255,239,0.72)" : "#667781"),
  quotedIn: (theme) => (isDark(theme) ? "#111827" : "#f0f2f5"),
  quotedOut: (theme) => (isDark(theme) ? "#064e3b" : "#cde9c7"),
  composerBg: (theme) => (isDark(theme) ? "#0b1220" : "transparent"),
  composerBar: (theme) => (isDark(theme) ? "#1f2937" : "#ffffff"),
};

// ── Fragmentos ────────────────────────────────────────────────────────────────

/** Superficie base: borde + sombra suave + radio + transición. */
export const cardSurface = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: BORDERS.base(theme),
  borderRadius: RADII.surface,
  boxShadow: SHADOWS.base(theme),
  transition: TRANSITION,
  [theme.breakpoints.down("sm")]: {
    borderRadius: RADII.surfaceSm,
  },
});

/** Elevación real al pasar el ratón. Pensado para elementos clicables. */
export const liftHover = (theme) => ({
  "&:hover": {
    borderColor: BORDERS.hover(theme),
    boxShadow: SHADOWS.lift(theme),
    transform: "translateY(-2px)",
  },
});

/** Anillo azul de marca al enfocar (`:focus-within`) o seleccionar (`.Mui-selected`). */
export const brandFocusRing = (theme) => ({
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: SHADOWS.ring(theme),
    backgroundColor: theme.palette.background.paper,
  },
  "&.Mui-selected": {
    borderColor: theme.palette.primary.main,
    backgroundColor: TINT.brand(theme),
    boxShadow: SHADOWS.ring(theme),
  },
});

/** Botón-icono compacto con superficie, hover elevado y acento de marca. */
export const iconControl = (theme) => ({
  minWidth: 32,
  height: 32,
  borderRadius: RADII.control,
  border: BORDERS.base(theme),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  transition: TRANSITION,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    boxShadow: SHADOWS.lift(theme),
    transform: "translateY(-2px)",
  },
  "& svg": { fontSize: 16 },
});

/** Chip / contador tipo píldora, con cifras tabulares. */
export const pill = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: RADII.pill,
  padding: "2px 10px",
  fontSize: "11px",
  fontWeight: 600,
  lineHeight: "18px",
  letterSpacing: "0.02em",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
});

// ── Punto de presencia ("Escribiendo…") ──────────────────────────────────────

/** Añadir a `makeStyles` para exponer el `@keyframes` (nombre: `redesignPulse`). */
export const presencePulseKeyframes = {
  "@keyframes redesignPulse": {
    "0%, 100%": { opacity: 0.35 },
    "50%": { opacity: 1 },
  },
};

/** Texto de presencia con punto que late. Respeta `prefers-reduced-motion`. */
export const presenceDot = (theme) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontStyle: "italic",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  "&::before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "currentColor",
    animation: "$redesignPulse 1.3s ease-in-out infinite",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::before": { animation: "none", opacity: 0.7 },
  },
});

// ── Modales del módulo ───────────────────────────────────────────────────────

/**
 * Superficie de un <Dialog> del módulo: mismo lenguaje que las tarjetas
 * (radio 16, borde suave, sombra elevada). Fragmento por si se necesita
 * componer a mano; lo habitual es usar `useDialogStyles`.
 */
export const dialogSurface = (theme) => ({
  borderRadius: 16,
  border: BORDERS.base(theme),
  boxShadow: SHADOWS.lift(theme),
  // Recorta cabeceras de color y contenido a las esquinas redondeadas.
  // Seguro con scroll="paper" (el scroll vive en DialogContent).
  overflow: "hidden",
  "&.MuiDialog-paperFullScreen": {
    borderRadius: 0,
    border: "none",
  },
  [theme.breakpoints.down("xs")]: {
    margin: 0,
    width: "100%",
    maxWidth: "100%",
    maxHeight: "min(92dvh, 100%)",
    borderRadius: "16px 16px 0 0",
  },
});

/**
 * Hook listo para usar en los modales del módulo:
 *   const dlg = useDialogStyles();
 *   <Dialog classes={{ paper: dlg.paper }} ...>
 */
export const useDialogStyles = makeStyles((theme) => ({
  paper: dialogSurface(theme),
}));
